import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'

import type { Contacts } from '@albacross/types'

import { get, type HttpError, isServerError, type QueryParams } from 'utils/http'

const useContactsRead = <T>(path: string, query: QueryParams) => {
  const customerId = useCustomerId()

  return useQuery<T, HttpError>({
    queryKey: ['customer', customerId, 'contacts_activity', path, query],
    queryFn: () => get<T>(`/management/customers/${customerId}/contacts/${path}`, { query }),
    placeholderData: keepPreviousData,
    // A 4xx won't change on retry, so a refused search shows its reason at once.
    retry: (failures, error) => isServerError(error.status) && failures < 3,
  })
}

export const useCustomerCreditSummary = (period: string) =>
  useContactsRead<Contacts.CreditSummary>('credits/summary', { period })

export const useCustomerCreditRequests = (query: QueryParams) =>
  useContactsRead<Contacts.ActivityPage<Contacts.CreditRequestItem>>('credits/requests', query)

export const useCustomerDeliveredPeople = (period: string, key: string) =>
  useContactsRead<Contacts.DeliveredPeople>('credits/requests/people', { period, key })

export const useCustomerCreditUnlocks = (query: QueryParams) =>
  useContactsRead<Contacts.ActivityPage<Contacts.CreditUnlock>>('credits/unlocks', query)
